/**
 * Krok 2: O firmie — Opis długi + Opis krótki + AI generator.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { industryName } from '../industries';

export default function Step2About({ data, updateData }) {
  const { token } = useAuth();
  const [generating, setGenerating] = useState(false);

  const handleAI = async () => {
    if (!data.name || !data.industry) {
      toast.error('Najpierw uzupełnij nazwę i branżę w kroku 1');
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/business/generate-description`,
        {
          name: data.name,
          industry: industryName(data.industry),
          city: data.city,
          services: data.services || [],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.ok) {
        updateData({
          description: res.data.description || data.description,
          short_description: res.data.short_description || data.short_description,
          seo_title: res.data.seo_title,
          seo_description: res.data.seo_description,
        });
        toast.success('Wygenerowano opis');
      } else {
        toast.error('Asystent chwilowo niedostępny');
      }
    } catch {
      toast.error('Nie udało się wygenerować opisu');
    } finally {
      setGenerating(false);
    }
  };

  const descLen = (data.description || '').length;
  const shortLen = (data.short_description || '').length;

  return (
    <div className="space-y-8" data-testid="business-wizard-v2-step-about">
      <header>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-orange-500" />
          </div>
          <span className="text-xs uppercase tracking-wider font-semibold text-orange-600">Krok 2 z 7</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Opowiedz o swojej firmie</h2>
        <p className="text-sm text-gray-500 mt-1">Krótki opis tego, czym się zajmujesz.</p>
      </header>

      {/* AI button */}
      <div className="bg-gradient-to-br from-orange-50 to-orange-50 border border-orange-200 rounded-2xl p-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-orange-900 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4" /> Napisz za mnie
          </p>
          <p className="text-xs text-gray-600 mt-0.5">
            Wygeneruję opis i meta na podstawie nazwy, branży i miasta.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={handleAI}
          disabled={generating}
          data-testid="bwv2-ai-generate-btn"
          className="bg-orange-500 hover:bg-orange-600 text-white flex-shrink-0"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? 'Generuję...' : 'Wygeneruj'}
        </Button>
      </div>

      {/* 1. Krótki opis */}
      <section>
        <div className="flex items-start gap-3 mb-2">
          <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
          <div className="flex-1">
            <Label className="text-base font-semibold text-gray-900">Hasło / slogan firmy</Label>
            <p className="text-xs text-gray-500 mt-0.5">Jedno zdanie, które wyświetli się w hero strony i w wyszukiwarce.</p>
          </div>
        </div>
        <div className="ml-10">
          <Input
            value={data.short_description || ''}
            onChange={(e) => updateData({ short_description: e.target.value })}
            placeholder="np. Profesjonalna stylizacja włosów w sercu Warszawy"
            maxLength={160}
            data-testid="bwv2-short-desc-input"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">{shortLen}/160</p>
        </div>
      </section>

      {/* 2. Pełny opis */}
      <section>
        <div className="flex items-start gap-3 mb-2">
          <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
          <div className="flex-1">
            <Label className="text-base font-semibold text-gray-900">Pełny opis firmy</Label>
            <p className="text-xs text-gray-500 mt-0.5">Min. 150 znaków. Powiedz co Cię wyróżnia, dla kogo pracujesz i jak wygląda obsługa.</p>
          </div>
        </div>
        <div className="ml-10">
          <Textarea
            value={data.description || ''}
            onChange={(e) => updateData({ description: e.target.value })}
            placeholder="Działamy od 2015 roku. Specjalizujemy się w..."
            rows={8}
            maxLength={2000}
            data-testid="bwv2-desc-input"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">{descLen}/2000</p>
        </div>
      </section>
    </div>
  );
}
